import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { GenericCustomDropdown } from '../_components/GenericCustomDropdown';
import { LOCATION_PERMISSIONS } from '../_constants/permissions.constants';
import { LocationModel } from '../_models/LocationModel';

import { invitationPermissionUiValues } from '../_constants';
import { CapsuleButton } from '../_components/CapsuleButton';
import { Icons } from '../_assets';

const testIds = {
    dropdown: "locations-dropdown",
    addLocationButton: "add-location-button",
    currentLocationButton: "current-location-button",
};

LocationsDropdown.testIds = testIds;

export function LocationsDropdown({
    locations = [] || [LocationModel()],
    handleLocationClick,
    selectedUid,
    selectedName,
    selectedId,
    handleAddLocation,
    isSearchBarActive = false,
}) {

    //LIBRARIES
    const { t } = useTranslation();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [filter, setFilter] = useState("");
    const inputRef = useRef(null);

    const keypressHandler = useCallback((e) => {
        if (inputRef.current) {
            inputRef.current?.focus();
        }
    },[inputRef]);

    const keydownHandler = useCallback((e) => {
        if (e.key === "Escape" && inputRef.current) {
            if (filter) {
                setFilter("");
            } else {
                inputRef.current?.blur();
            }
        }
    },[filter, inputRef]);

    //EFFECTS
    useEffect(() => {
        if (!isSearchBarActive) {
            return;
        }

        if (isDropdownOpen) {
            window.addEventListener("keypress", keypressHandler);
            window.addEventListener("keydown", keydownHandler);
        }

        return () => {
            window.removeEventListener("keypress", keypressHandler);
            window.removeEventListener("keydown", keydownHandler);
        }
    }, [keypressHandler, keydownHandler, isDropdownOpen, isSearchBarActive]);

    //EFFECTS
    useEffect(() => {
        if (!isSearchBarActive) {
            return;
        }

        if (!isDropdownOpen) {
            setFilter("");
        }
    }, [isDropdownOpen, isSearchBarActive]);

    //VIEW
    return (
        <GenericCustomDropdown
            testid={testIds.dropdown}
            selectedOptionFn={selectedLocation}
            optionsListFn={locationsDropDown}
            relativeToExternalParent={true}
            onOpenChange={handleDropdownOpenChange}
        />
    );

    //LOCAL HANLDERS
    function handleClickSearch(e) {
        e.stopPropagation();
        inputRef.current?.focus();
    }

    function handleClickClear(e) {
        e.stopPropagation();
        setFilter("");
        inputRef.current?.focus();
    }

    function handleDropdownOpenChange(isOpen) {
        setIsDropdownOpen(isOpen);
    }

    function selectedLocation() {
        return (
            <div
                data-testid={testIds.currentLocationButton}
                className="
                    d-flex
                    no-gutters
                    align-items-center
                    justify-content-between

                    overflow-auto

                    text-center
                    pl-3
                    pr-2
                    py-1
                    cp
                    c-cursor-default
                    c-bg-grey-250
                    c-hover-220
                    c-rounded-capsule
                "
            >
                <div className="
                    text-truncate
                    font-weight-bold
                    text-uppercase
                ">
                    {selectedName
                        ? (selectedId != null ? `${selectedName} (${selectedId})` : selectedName)
                        : t("locations.selectALocation")}
                </div>

                <div>
                    <div className="ml-1"/>
                    <img
                        src={Icons.arrowDown}
                        alt="dropdown icon"
                        className="c-img-1-5rem"
                    />
                </div>
            </div>
        );
    }
    
    function locationsDropDown(isOpen = false) {
        return (
            <div className={`

                c-mh-30rem
                c-custom-dropdown

                d-flex
                flex-column

                position-absolute
                
                mt-2
                px-2
                pb-2

                rounded
                c-box-shadow
                bg-white

                overflow-hidden
                c-z-index-full
                ${vScale()}
                c-transition-all
            `}>
                {addLocationButton()}
                {isSearchBarActive && searchBar()}
                <div className="overflow-auto">
                    {locationsButtons()}
                </div>
            </div>
        );

        function vScale() {
            return isOpen
                ? "c-v-scale-100 c-opacity-100"
                : "c-v-scale-0 c-opacity-0"
            ;
        }
    }

    function addLocationButton() {
        return (
            <div className="
                d-flex
                justify-content-between
                align-items-center
                py-2
            ">
                {locationsTitle()}
                <CapsuleButton
                    testId={testIds.addLocationButton}
                    text={t("webapp_global_create")}
                    onClick={handleAddLocation}
                />
            </div>
        );

        function locationsTitle() {
            return (
                <div className="
                    pl-2
                    font-weight-bold
                    text-uppercase
                ">
                    {t("common.locations")}
                </div>
            );
        }
    }

    function filterLocationsBySearch(locations) {
        return locations.filter(option => option.name.toLowerCase().includes(filter.toLowerCase()));
    }

    function locationsButtons() {
        let filteredLocations = locations;
        if (filter) {
            filteredLocations = filterLocationsBySearch(locations);
        }

        return (
            sortLocations(filteredLocations)
            .map((location, index) =>
                uiSingleLocation(
                    index,
                    location.id != null ? `${location.name} (${location.id})` : location.name,
                    invitationPermissionUiValues[location.userType],
                    () => handleLocationClick(location),
                    location.uid === selectedUid,
                )
            )
        );

        function sortLocations(locations = [LocationModel()]) {
            const allSortedLocations = [];

            const orderedPermissions = [
                LOCATION_PERMISSIONS.OWNER,
                LOCATION_PERMISSIONS.ADMIN,
                LOCATION_PERMISSIONS.INSTALLER,
                LOCATION_PERMISSIONS.GUEST,
                LOCATION_PERMISSIONS.NO_ACCESS,
            ];

            for (let i = 0; i < orderedPermissions.length; i++) {
                const sortedLocations =
                    locations
                    .filter(location =>
                        location.userType === orderedPermissions[i]
                    )
                    .sort((a = LocationModel(), b = LocationModel()) =>
                        a.name.localeCompare(b.name)
                    )
                ;

                allSortedLocations.push(...sortedLocations);
            }

            return allSortedLocations;
        }
    }

    function uiSingleLocation(index, tag, permission, onClick, isSelected) {
        return (
            <div
                key={index}>
                <hr className="m-0 mx-1"/>
                <div
                    data-testid={tag}
                    onClick={onClick}
                    className={`
                        d-flex
                        justify-content-between
                        no-gutters
                        
                        c-hover-240

                        font-weight-normal
                        
                        cp
                        
                        px-2
                        py-1
                        mx-1

                        ${isSelected && "c-bg-grey-230"}
                    `}
                >
                    <div>
                        {tag}
                    </div>
                    <div>
                        {permission}
                    </div>
                </div>
            </div>
        );
    }

    function searchBar() {
        return (
            <div className="d-flex flex-row my-2 mx-1 border rounded py-1 px-2" onClick={handleClickSearch}>
                <input
                    ref={inputRef}
                    onChange={(e) => setFilter(e.target.value)}
                    type="text"
                    value={filter}
                    placeholder={t("global_search_placeholder")}
                    className="
                        w-100
                        p-0
                        
                        c-no-focus-outline
                        border-0
                    "
                />
                {filter ? (
                    <div
                        onClick={handleClickClear}
                        className="
                            c-hover-220
                            my-auto
                            px-1
                            rounded
                            cp
                        "
                    >
                        <img
                            className="c-img-1_5rem"
                            src={Icons.close}
                            alt={"clear_img"}
                        />
                    </div>
                ) : (
                    <div className="my-auto px-1">
                        <img
                            className="c-img-1_5rem"
                            src={Icons.search}
                            alt={"search_img"}
                        />
                    </div>
                )}
            </div>
        )
    }
}